import { Cell, Grid, TabItem, Tabs } from 'bold-ui'
import CheckPermission from 'components/auth/CheckPermission'
import useSession from 'components/auth/useSession'
import { useFlags } from 'config/useFlagsContext'
import React, { useState } from 'react'
import Permissions from 'types/Permissions'
import { getTiposEstabelecimentoCuidadoCompartilhado } from 'view/cuidados-compartilhados/util-cuidadoCompartilhado'

import {
  grupoCboAtestado,
  grupoCboCuidadoCompartilhado,
  grupoCboEncaminhamentoExterno,
  grupoCboOrientacoes,
  grupoCboPrescricao,
  grupoCboSolicitacaoExame,
} from '../acessos'

export enum PlanoTabsEnum {
  PRESCRICAO_MEDICAMENTOS,
  ATESTADO,
  EXAMES,
  ENCAMINHAMENTO_EXTERNO,
  DECLARACAO_COMPARECIMENTO,
  ORIENTACOES,
  CUIDADO_COMPARTILHADO,
}

interface PlanoTabsProps {
  isAtendimentoProcedimentos: boolean
  isFolhaRosto?: boolean
  isObservacaoAndAuxiliar?: boolean
  isRetificacao?: boolean
  isAtendimentoOdonto?: boolean
  children: (currentTab: PlanoTabsEnum) => React.ReactNode
}

export const SOLICITACAO_EXAMES_PROCEDIMENTOS_TAB_NAME = 'Solicitação de exames/procedimentos'

export const PlanoTabs = (props: PlanoTabsProps) => {
  const {
    isAtendimentoProcedimentos,
    isFolhaRosto,
    isObservacaoAndAuxiliar,
    isRetificacao = false,
    isAtendimentoOdonto,
    children,
  } = props

  const { PILOTO_VINCULACAO_CUIDADO_ENABLED, NOVA_ABA_ENCAMINHAMENTO_ENABLED } = useFlags()
  const { hasAuthorization, hasCboAuth } = useSession({ fetchPolicy: 'cache-only' })

  const [currentPlanoTab, setCurrentPlanoTab] = useState(
    !isRetificacao ? PlanoTabsEnum.PRESCRICAO_MEDICAMENTOS : PlanoTabsEnum.ORIENTACOES
  )

  const showAtestado = !isAtendimentoProcedimentos && hasCboAuth(grupoCboAtestado.visualizar)
  const showDeclaracao =
    !isRetificacao &&
    !showAtestado &&
    hasAuthorization(Permissions.visualizarListaDeAtendimento.gerarDeclaracaoDeComparecimento)
  const showPrescricao = !isRetificacao && hasCboAuth(grupoCboPrescricao.visualizar)
  const showExames = !isRetificacao && hasCboAuth(grupoCboSolicitacaoExame.visualizar)
  const showEncaminhamento = !isAtendimentoProcedimentos && (!NOVA_ABA_ENCAMINHAMENTO_ENABLED || isAtendimentoOdonto)
  const showCuidadoCompartilhado =
    !(isObservacaoAndAuxiliar || isAtendimentoProcedimentos) &&
    (PILOTO_VINCULACAO_CUIDADO_ENABLED || hasCboAuth(grupoCboCuidadoCompartilhado))

  return (
    <Grid>
      <Cell size={12}>
        <Tabs>
          {showPrescricao && (
            <TabItem
              defaultChecked
              active={currentPlanoTab === PlanoTabsEnum.PRESCRICAO_MEDICAMENTOS}
              onClick={() => {
                setCurrentPlanoTab(PlanoTabsEnum.PRESCRICAO_MEDICAMENTOS)
              }}
            >
              Prescrição de medicamentos
            </TabItem>
          )}
          {showExames && (
            <TabItem
              active={currentPlanoTab === PlanoTabsEnum.EXAMES}
              onClick={() => setCurrentPlanoTab(PlanoTabsEnum.EXAMES)}
            >
              {SOLICITACAO_EXAMES_PROCEDIMENTOS_TAB_NAME}
            </TabItem>
          )}
          {showAtestado && (
            <TabItem
              active={currentPlanoTab === PlanoTabsEnum.ATESTADO}
              onClick={() => setCurrentPlanoTab(PlanoTabsEnum.ATESTADO)}
            >
              Atestados
            </TabItem>
          )}
          <CheckPermission permission={grupoCboOrientacoes.visualizar}>
            <TabItem
              active={currentPlanoTab === PlanoTabsEnum.ORIENTACOES}
              onClick={() => setCurrentPlanoTab(PlanoTabsEnum.ORIENTACOES)}
            >
              Orientações
            </TabItem>
          </CheckPermission>
          {showEncaminhamento && (
            <CheckPermission permission={grupoCboEncaminhamentoExterno}>
              <TabItem
                active={currentPlanoTab === PlanoTabsEnum.ENCAMINHAMENTO_EXTERNO}
                onClick={() => setCurrentPlanoTab(PlanoTabsEnum.ENCAMINHAMENTO_EXTERNO)}
              >
                Encaminhamentos
              </TabItem>
            </CheckPermission>
          )}
          {showDeclaracao && !isFolhaRosto && (
            <TabItem
              active={currentPlanoTab === PlanoTabsEnum.DECLARACAO_COMPARECIMENTO}
              onClick={() => setCurrentPlanoTab(PlanoTabsEnum.DECLARACAO_COMPARECIMENTO)}
            >
              Declaração de comparecimento
            </TabItem>
          )}

          {showCuidadoCompartilhado && (
            <CheckPermission
              permission={grupoCboCuidadoCompartilhado}
              tiposEstabelecimento={getTiposEstabelecimentoCuidadoCompartilhado(PILOTO_VINCULACAO_CUIDADO_ENABLED)}
              acceptEstagio={false}
            >
              <TabItem
                active={currentPlanoTab === PlanoTabsEnum.CUIDADO_COMPARTILHADO}
                onClick={() => setCurrentPlanoTab(PlanoTabsEnum.CUIDADO_COMPARTILHADO)}
              >
                {NOVA_ABA_ENCAMINHAMENTO_ENABLED && !isAtendimentoOdonto ? (
                  <>Encaminhamento/Compartilhamento de cuidado</>
                ) : (
                  'Compartilhamento de cuidado'
                )}
              </TabItem>
            </CheckPermission>
          )}
        </Tabs>
      </Cell>
      <Cell size={12}>{children(currentPlanoTab)}</Cell>
    </Grid>
  )
}
